import React, { useState, useEffect, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { getListings } from '../../service/Listings/getListings';
import { addOpenTime } from '../../service/OFI/postOpentimes';
import { getReminderDate} from '../../service/Util/dateTimeUtil';


const validationSchema = Yup.object().shape({
  propertyId: Yup.string().required('Property is required'),
  selectedDate: Yup.string().required('Date is required'),
  start: Yup.string().required('Start time is required'),
  end: Yup.string().required('End time is required').test('is-greater', 'End time must be after start time', function(value) {
    const { start } = this.parent;
    if (!start || !value) return true;
    const timeToMinutes = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };      
    return timeToMinutes(value) > timeToMinutes(start);
  })
});

const methodOfCommunication = [
  { label: 'Email First', value: '1' },
  { label: 'SMS First', value: '2' },
  { label: 'Email & SMS', value: '3' },
];

const reminderDayOptions = [
  { label: '1 day', value: '1' }, 
  { label: '2 day', value: '2' }
];

const OFIAdd = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [isSavingOpenTime, setIsSavingOpenTime] = useState(false);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await getListings();
      const propertiesWithFormattedAddress = response.data.map(property => ({
        ...property,
        formattedAddress: `${property.streetNumber || ''} ${property.streetName || ''} ${property.suburb || ''} ${property.state || ''} ${property.postCode || ''}`
      }));
      setProperties(propertiesWithFormattedAddress);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleSubmit = async (values) => {
    setIsSavingOpenTime(true);
    try {
      let endTime24 = values.end;
      let reminderDate = '';
      if (values.byAppointment === 1) {
        endTime24 = values.start;
      }
      const startTime = `${values.selectedDate} ${values.start}`;
      const endTime = `${values.selectedDate} ${endTime24}`;
      if (values.setReminder === 1) {
        reminderDate = getReminderDate(values.selectedDate, values.reminderdaysBefore);
      }
      const openTimeJSON = {
        ...values,
        startTime: startTime,
        endTime: endTime,
        reminderDate: reminderDate,
      };
      const response = await addOpenTime(openTimeJSON);
      if (response.status === 200 || response.status === 201) {
        navigate('/ofi-view');
      }
      setIsSavingOpenTime(false);
    } catch (error) {
      setIsSavingOpenTime(false);
    }
  };

  const CustomDropdown = ({ data, value, onChange, placeholder, labelField, valueField, searchable = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const filteredData = searchable 
      ? data.filter(item => 
          item[labelField].toLowerCase().includes(searchTerm.toLowerCase())
        )
      : data;

    const selectedItem = data.find(item => item[valueField] === value);

    return (
      <div className="dropdown-container">
        <div 
          className="dropdown-header"
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{selectedItem ? selectedItem[labelField] : placeholder}</span>
          <span className={`dropdown-arrow ${isOpen ? 'open' : ''}`}>▼</span>
        </div>
        {isOpen && (
          <div className="dropdown-menu">
            {searchable && (
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="dropdown-search"
              />
            )}
            {filteredData.map((item) => (
              <div
                key={item[valueField]}
                className="dropdown-item"
                onClick={() => {
                  onChange(item);
                  setIsOpen(false);
                  setSearchTerm('');
                }}
              >
                {item[labelField]}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const CustomSwitch = ({ checked, onChange, label }) => (
    <div className="switch-container">
      <span className="switch-label">{label}</span>
      <label className="switch">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span className="slider"></span>
      </label>
    </div>
  );

  return (
    <Formik
      initialValues={{
        propertyId: '',
        start: '',
        end: '',
        startAmPm: '',
        endAmPm: '',
        byAppointment: 0,
        recurringWeekly: 0,
        preferredcommunicationType: '',
        selectedDate: new Date().toISOString().split('T')[0],
        reminderdaysBefore: '',
        setReminder: 0,
        setCustomOFIText: 0,
        customOFIEmailText: '',
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ handleChange, handleBlur, handleSubmit, values, errors, touched, setFieldValue }) => (
        <div className="add-opentimes-container">
          <div className="scroll-content">
            <div className="form-container">
              
              {/* Property Section */}
              <div className="form-section">
                <h3 className="section-title">Property</h3>
                <CustomDropdown
                  data={properties}
                  labelField="formattedAddress"
                  valueField="propertyId"
                  placeholder="Select Property"
                  searchable={true}
                  value={values.propertyId}
                  onChange={(item) => setFieldValue('propertyId', item.propertyId)}
                />
                {touched.propertyId && errors.propertyId && (
                  <div className="error-text">{errors.propertyId}</div>
                )}
              </div>

              {/* Communication Section */}
              <div className="form-section">
                <h3 className="section-title">Preferred Communication Type</h3>
                <CustomDropdown
                  data={methodOfCommunication}
                  labelField="label"
                  valueField="value"
                  placeholder="Preferred Communication Type"
                  value={values.preferredcommunicationType}
                  onChange={(item) => setFieldValue('preferredcommunicationType', item.value)}
                />
              </div>

              {/* Calendar Section */}
              <div className="form-section">
                <h3 className="section-title">Select Date</h3>
                <div className="date-input-container">
                  <input
                    type="date"
                    value={values.selectedDate}
                    onChange={(e) => setFieldValue('selectedDate', e.target.value)}
                    className="date-input"
                  />
                </div>
                {touched.selectedDate && errors.selectedDate && (
                  <div className="error-text">{errors.selectedDate}</div>
                )}
              </div>

              {/* Start Time Section */}
              <div className="form-section">
                <h3 className="section-title">Start Time</h3>
                <input
                  type="time"
                  value={values.start}
                  onChange={(e) => setFieldValue('start', e.target.value)}
                  className="time-input"
                  step="300"
                />
                {touched.start && errors.start && (
                  <div className="error-text">{errors.start}</div>
                )}
              </div>

              {/* End Time Section */}
              {values.byAppointment !== 1 && (
                <div className="form-section">
                  <h3 className="section-title">End Time</h3>
                  <input
                    type="time"
                    value={values.end}
                    onChange={(e) => setFieldValue('end', e.target.value)}
                    className="time-input"
                    step="300"
                  />
                  {touched.end && errors.end && (
                    <div className="error-text">{errors.end}</div>
                  )}
                </div>
              )}

              {/* Options Section */}
              <div className="form-section">
                <h3 className="section-title">Options</h3>
                <div className="switches-container">
                  <CustomSwitch
                    label="IBA"
                    checked={values.byAppointment === 1}
                    onChange={(checked) => {
                      setFieldValue('byAppointment', checked ? 1 : 0);
                      if (checked) {
                        setFieldValue('end', values.start);
                        setFieldValue('endAmPm', values.startAmPm);
                      }
                    }}
                  />
                  <CustomSwitch
                    label="Recurring Weekly"
                    checked={values.recurringWeekly === 1}
                    onChange={(checked) => setFieldValue('recurringWeekly', checked ? 1 : 0)}
                  />
                </div>

                <div className="reminder-row">
                  <CustomSwitch
                    label="Reminder"
                    checked={values.setReminder === 1}
                    onChange={(checked) => setFieldValue('setReminder', checked ? 1 : 0)}
                  />
                  {values.setReminder === 1 && (
                    <div className="reminder-dropdown">
                      <CustomDropdown
                        data={reminderDayOptions}
                        labelField="label"
                        valueField="value"
                        placeholder="Reminder Days"
                        value={values.reminderdaysBefore}
                        onChange={(item) => setFieldValue('reminderdaysBefore', item.value)}
                      />
                    </div>
                  )}
                </div>

                <CustomSwitch
                  label="Custom OFI Email Text"
                  checked={values.setCustomOFIText === 1}
                  onChange={(checked) => setFieldValue('setCustomOFIText', checked ? 1 : 0)}
                />

                {values.setCustomOFIText === 1 && (
                  <div className="custom-text-section">
                    <textarea
                      placeholder="Custom OFI Email Text"
                      value={values.customOFIEmailText}
                      onChange={(e) => {
                        const cleanedText = e.target.value.replace(/[^\w\s.,?!'"@-]/g, '').slice(0, 1000);
                        setFieldValue('customOFIEmailText', cleanedText);
                      }}
                      className="custom-text-input"
                      rows="5"
                    />
                    <div className="helper-text">
                      * Max 1000 Characters, special characters are not allowed. You have entered {values.customOFIEmailText.length} characters.
                    </div>
                  </div>
                )}
              </div>
            </div>
              <div className="footer">
            <button
              type="button"
              onClick={handleSubmit}
              className="submit-button"
              disabled={isSavingOpenTime}
            >
              {isSavingOpenTime ? 'Saving...' : 'Save OpenTime'}
            </button>
          </div>
</div>
        
        </div>
      )}
    </Formik>
  );
};

export default OFIAdd;
